<template>
  <div
    class="container"
    @click="onClickStock"
  >
    <stock-card
      :hotel-id="hotel.id"
      large
      :available="isAvailable(hotel.id)"
    />
    <div class="hotel-name">{{ hotel.id }}</div>
    <div
      class="hotel-price"
      :class="{
        'unavailable': !isAvailable(hotel.id)
      }"
    >
      {{ stockPrice | formatCurrency }}
    </div>
    <div
      class="hotel-quantity"
      :class="{
        'unavailable-qty': !isAvailable(hotel.id)
      }"
    >
      {{ hotel.quantity }}
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import StockCard from "@/components/StockCard";
import TooManyStocks from "@/exceptions/TooManyStocks";
import { formatCurrency } from "@/currency-filter";
import { mapActions, mapGetters } from 'vuex';

import {
  NS_GAME_STATE,
  NS_LAYER_STATE,
} from "@/store/constants";
import { createStocksState } from "@/store/game/schema";
import InsufficientFunds from "@/exceptions/InsufficientFunds";
import NoStocksAvailable from "@/exceptions/NoStocksAvailable";
import HotelUnavailable from "@/exceptions/HotelUnavailable";

export default {
  name: 'StocksAvailableCard',

  props: {
    hotel: {
      type: Object,
      default: createStocksState(),
    }
  },

  components: {
    StockCard,
  },

  filters: {
    formatCurrency,
  },

  computed: {
    ...mapGetters(NS_GAME_STATE, [
      'isMyTurn',
      'isBuyingStocks',
      'isMerger',
      'hasPlacedTile',
      'stockMoneyPerHotel',
      'tilesPerHotel',
    ]),

    stockPrice() {
      return this.stockMoneyPerHotel[this.hotel.id].price;
    }
  },

  methods: {
    ...mapActions(NS_GAME_STATE, [
      'addStockToBePurchased',
    ]),

    ...mapActions(NS_LAYER_STATE, [
      'addToast',
      'clearToasts',
    ]),

    isAvailable(hotelId) {
      const count = get(this, `tilesPerHotel[${hotelId}].length`, 0);
      return count > 0;
    },

    hasErrors() {
      if (!this.isMyTurn) {
        this.addToast({ message: 'You can only purchase stocks on your turn' });
        return true;
      }

      if (!this.hasPlacedTile) {
        this.addToast({ message: 'Please place a tile first' });
        return true;
      }

      if (this.isMerger) {
        this.addToast({ message: "You can't purchase stocks while a merger is taking place. Please wait for other players to complete the merger." });
        return true;
      }

      if (!this.isBuyingStocks) {
        this.addToast({ message: "You must place a tile before you can purchase stocks" });
        return true;
      }

      return false;
    },

    async onClickStock() {
      this.clearToasts();

      if (this.hasErrors()) {
        return;
      }

      try {
        await this.addStockToBePurchased(this.hotel.id);
      } catch (e) {
        if (e instanceof TooManyStocks) {
          this.addToast({ message: "You can't buy more than 3 stocks per turn" });
          return;
        }
        if (e instanceof InsufficientFunds) {
          this.addToast({ message: "You can't afford to purchase this stock" });
          return;
        }
        if (e instanceof NoStocksAvailable) {
          this.addToast({ message: "There are no stocks left for this company" });
          return;
        }
        if (e instanceof HotelUnavailable) {
          this.addToast({ message: "This company has not been placed on the board yet. Place two tiles next to each other to build a new company" });
          return;
        }
        throw e;
      }
    },
  }
}
</script>

<style scoped>
.container {
  position: relative;
}

.hotel-quantity,
.hotel-price
{
  font-weight: 600;
  padding: 3px;
  text-align: center;
}

.hotel-quantity {
  background: #a00000;
  color: white;
  box-shadow: 2px 2px 2px #0000008c;
  border-radius: 50%;
  position: absolute;
  top: -7px;
  left: -4px;
  padding: 5px;
  padding-top: 5px;
  width: 20px;
  height: 20px;
  text-align: center;
  justify-content: center;
  align-content: center;
  line-height: 23px;
}

.hotel-name {
  font-size: 12px;
  text-align: center;
  padding-bottom: 5px;
  padding-right: 3px;
}

.unavailable {
  font-weight: 300;
  color: grey;
}

.unavailable-qty {
  box-shadow: 0 0 0;
  background: #b3b3b3;
}
</style>
